/**
 * User Service
 * Handles user management API calls
 */

import { api } from '../lib/axios';

export interface PendingUser {
  id: string;
  email: string;
  fullName: string;
  phone: string;
  roleLevel: number;
  createdAt: string;
}

export interface User {
  id: string;
  email?: string;
  fullName: string;
  phone: string;
  roleLevel: number;
  status: string;
  createdAt: string;
  parentUserId?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
}

export interface InviteUserData {
  fullName: string;
  email: string;
  phone: string;
  roleLevel: number;
  parentUserId?: string;
}

export interface AddDealerData {
  fullName: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  roleLevel: number;
  parentUserId?: string;
  email?: string;
}

class UserService {
  /**
   * Get pending user approvals
   */
  async getPendingApprovals(): Promise<PendingUser[]> {
    const response = await api.get('/users/pending-approvals');
    return response.data.pendingApprovals;
  }

  /**
   * Approve a user
   */
  async approveUser(userId: string): Promise<void> {
    await api.post(`/users/${userId}/approve`);
  }

  /**
   * Reject a user
   */
  async rejectUser(userId: string, reason?: string): Promise<void> {
    await api.post(`/users/${userId}/reject`, { reason });
  }

  /**
   * Get user's children (direct reports)
   */
  async getUserChildren(): Promise<any[]> {
    const response = await api.get('/users/children');
    return response.data.children;
  }

  /**
   * Get user hierarchy
   */
  async getUserHierarchy(): Promise<any> {
    const response = await api.get('/users/hierarchy');
    return response.data.hierarchy;
  }

  /**
   * Get approved users for a manufacturer
   */
  async getApprovedUsers(manufacturerId: string): Promise<{ success: boolean; data?: any[] }> {
    const response = await api.get(`/users/manufacturer/${manufacturerId}/approved`);
    return response.data;
  }

  /**
   * Get all dealers in the network (deprecated - use dealerService instead)
   */
  async getNetworkDealers(): Promise<User[]> {
    const response = await api.get('/dealers');
    return response.data.dealers || response.data;
  }

  /**
   * Add a new dealer to the network (using extended dealer system)
   */
  async addDealer(dealerData: AddDealerData): Promise<void> {
    await api.post('/dealers', {
      name: dealerData.fullName,
      mobileNumber: dealerData.phone,
      address: dealerData.address,
      city: dealerData.city,
      state: dealerData.state,
      pincode: dealerData.pincode,
      email: dealerData.email,
      roleLevel: dealerData.roleLevel,
      parentDealerId: dealerData.parentUserId, // Map parentUserId to parentDealerId
      contactPerson: dealerData.fullName,
    });
  }

  /**
   * Remove dealer from network (deprecated - use dealerService instead)
   */
  async removeDealer(dealerId: string): Promise<void> {
    await api.delete(`/dealers/${dealerId}`);
  }

  /**
   * Assign package to dealer
   */
  async assignPackageToDealer(packageId: string, dealerId: string): Promise<void> {
    await api.post(`/packages/${packageId}/assign`, { dealerId });
  }

  /**
   * Verify OTP and activate package for dealer
   */
  async verifyOTPAndActivatePackage(packageId: string, otp: string, phone: string): Promise<any> {
    const response = await api.post(`/packages/${packageId}/verify-otp`, { otp, phone });
    return response.data;
  }
}

export const userService = new UserService();
